import React, { useCallback, useState } from 'react';
import { Modal } from '@vectord/components';
import { useDispatch, useSelector } from 'react-redux';
import cogoToast from 'cogo-toast';

import { ASSET_UPLOAD_URL } from '@assetManagement/data/assetUpload';
import {
  setUploadVideoModalState,
  setVideoUploadSuccess,
} from '@assetManagement/store/uploadVideo';
import { uploadVideoModalStateSelector } from '@assetManagement/selectors/uploadVideo';
import getVideoDuration from '@assetManagement/utils/getVideoDuration';
import styles from './UploadVideoModal.module.scss';
import UploadToS3 from '@assetManagement/utils/uploadToS3';
import UploadVideoButton from '../UploadVideoButton';
import UploadVideoModalBody from '../UploadVideoModalBody';

export default function UploadVideoModal() {
  const dispatch = useDispatch();

  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const isUploadVideoModalOpen = useSelector(uploadVideoModalStateSelector);

  const handleVideoUpload = useCallback(async () => {
    if (isUploading) return;

    const uploadToS3 = new UploadToS3();
    try {
      setIsUploading(true);
      const videoDuration = await getVideoDuration(selectedFile);
      const { success } = await uploadToS3.uploadAttachment(
        ASSET_UPLOAD_URL,
        selectedFile,
        { video_duration: videoDuration }
      );
      if (success) {
        cogoToast.success('File Uploaded Successfully');
        setSelectedFile(null);
        dispatch(setVideoUploadSuccess(true));
      }
    } catch (err) {
      cogoToast.error(
        'Error while uploading file, please try again in sometime.'
      );
    } finally {
      setIsUploading(false);
    }
  }, [isUploading, selectedFile, dispatch]);

  const onSelectFile = useCallback((file) => {
    setSelectedFile(file);
  }, []);

  const resetFile = useCallback(() => {
    setSelectedFile(null);
  }, []);

  const handleModalClose = useCallback(() => {
    resetFile();
    dispatch(setUploadVideoModalState(false));
  }, [dispatch, resetFile]);

  return (
    <Modal
      isOpen={isUploadVideoModalOpen}
      size="large"
      title="Upload Video"
      unMountOnClose
      onClose={handleModalClose}
    >
      <UploadVideoModalBody
        selectedFile={selectedFile}
        onSelectFile={onSelectFile}
        resetFile={resetFile}
      />
      <div className={styles.uploadVideoModalFooter}>
        <UploadVideoButton
          onClick={handleVideoUpload}
          isDisabled={!selectedFile}
        >
          {isUploading ? 'Uploading...' : 'Upload Video'}
        </UploadVideoButton>
      </div>
    </Modal>
  );
}
